import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  inject,
} from '@angular/core';
import {
  NgForm,
  NonNullableFormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ContactFormPayload, ContactFormService } from '@speedy/core';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { SpeedyButtonComponent } from '../../../shared/components/speedy-button/speedy-button.component';
import { SpeedyTextareaComponent } from '../../../shared/components/speedy-textarea/speedy-textarea.component';
import { SpeedyInputComponent } from '../../../shared/components/speedy-input/speedy-input.component';
import { BlockComponent } from '../../../shared/components/block/block.component';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';

@Component({
  selector: 'speedy-contact-page',
  templateUrl: './contact-page.component.html',
  styleUrls: ['./contact-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    BlockComponent,
    ReactiveFormsModule,
    SpeedyInputComponent,
    SpeedyTextareaComponent,
    SpeedyButtonComponent,
  ],
})
export class ContactPageComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly contact = inject(ContactFormService);
  private readonly alertService = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('contactFormRef') contactFormRef: NgForm | undefined;

  contactForm = this.fb.group({
    name: this.fb.control(null, [Validators.required]),
    email: this.fb.control(null, [Validators.email, Validators.required]),
    phone: this.fb.control(null, [Validators.required, Validators.minLength(7)]),
    message: this.fb.control(null),
  });

  isContactFormLoading = false;

  onSubmit(): void {
    this.isContactFormLoading = true;

    if (this.contactForm.invalid) {
      this.isContactFormLoading = false;
      return;
    }

    const payload: ContactFormPayload = {
      name: this.contactForm.value.name ?? '',
      email: this.contactForm.value.email ?? '',
      telephone: this.contactForm.value.phone ?? '',
      comment: this.contactForm.value.message ?? '',
    };

    this.contact
      .sendContactForm(payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isContactFormLoading = false;
          this.cdr.markForCheck();
          this.contactFormRef?.resetForm();
          this.alertService.success('Your message has been sent');
        },
        error: () => {
          this.isContactFormLoading = false;
          this.alertService.error('There was an error sending your message');
        },
      });
  }
}
